import type {QRCalendarEventInput, QRPayloadType, QRWifiInput} from '@Libraries/qr-code/qr-code.types';
import {QRCodeDotType, QRCodeEyeBallType, QRCodeEyeFrameType, QRWifiSecurity} from '@Libraries/qr-code/qr-code.types';
import type {QRCodeStyle, QRGeneratorState} from '@Components/qr-code-generator/qr-code-generator.types';
import {getInitialQRGeneratorState, QR_TOOL_TYPES} from '@Components/qr-code-generator/qr-code-generator.types';

const QR_SESSION_STORAGE_KEY = 'pmwQrGenerator';
const QR_SESSION_VERSION = 1;

interface QRStoredSession {
  version: number;
  state: QRGeneratorState;
}

const getStorableState = (state: QRGeneratorState): QRGeneratorState => {
  return {...state, logo: null, wifiInput: {...state.wifiInput, password: ''}};
};

const getRestoredCalendarEventInput = (fallback: QRGeneratorState, stored: QRGeneratorState): QRCalendarEventInput => {
  return {...fallback.calendarEventInput, ...stored.calendarEventInput, generatedAt: fallback.calendarEventInput.generatedAt};
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const getRestoredValue = (fallbackValue: unknown, storedValue: unknown): unknown => {
  if (isPlainObject(fallbackValue) && isPlainObject(storedValue)) {
    return {...fallbackValue, ...storedValue};
  }
  if (storedValue === undefined || storedValue === null || typeof storedValue !== typeof fallbackValue || Array.isArray(storedValue) !== Array.isArray(fallbackValue)) {
    return fallbackValue;
  }
  return storedValue;
};

const getRestoredActiveType = (storedActiveType: unknown, fallbackActiveType: QRPayloadType): QRPayloadType => {
  return (
    QR_TOOL_TYPES.find((offeredType): boolean => {
      return offeredType === storedActiveType;
    }) ?? fallbackActiveType
  );
};

/**
 * Storage is untrusted: it survives a deploy that renames an enum member, and a visitor can edit it. A value that merely has the right
 * primitive type still reaches the payload builders, where an unrecognised member indexes a token map to `undefined` and silently emits a
 * corrupt code, so each enum-valued field is checked against its own members rather than its type.
 */
const getRestoredEnumValue = <T extends string>(storedValue: unknown, allowedValues: T[], fallbackValue: T): T => {
  return allowedValues.find((allowedValue): boolean => {
    return allowedValue === storedValue;
  }) ?? fallbackValue;
};

const getRestoredWifiInput = (fallback: QRWifiInput, restored: QRWifiInput): QRWifiInput => {
  return {...restored, security: getRestoredEnumValue(restored.security, Object.values(QRWifiSecurity), fallback.security)};
};

const getRestoredStyle = (fallback: QRCodeStyle, restored: QRCodeStyle): QRCodeStyle => {
  return {
    ...restored,
    dotStyle: getRestoredEnumValue(restored.dotStyle, Object.values(QRCodeDotType), fallback.dotStyle),
    eyeFrameStyle: getRestoredEnumValue(restored.eyeFrameStyle, Object.values(QRCodeEyeFrameType), fallback.eyeFrameStyle),
    eyeBallStyle: getRestoredEnumValue(restored.eyeBallStyle, Object.values(QRCodeEyeBallType), fallback.eyeBallStyle),
  };
};

const getRestoredState = (fallback: QRGeneratorState, stored: Record<string, unknown>): QRGeneratorState => {
  const restored: Record<string, unknown> = {};

  Object.entries(fallback).forEach(([stateKey, fallbackValue]): void => {
    restored[stateKey] = getRestoredValue(fallbackValue, stored[stateKey]);
  });

  const restoredState = restored as unknown as QRGeneratorState;

  return {
    ...restoredState,
    wifiInput: getRestoredWifiInput(fallback.wifiInput, restoredState.wifiInput),
    style: getRestoredStyle(fallback.style, restoredState.style),
  };
};

export const readQRGeneratorSession = (generatedAt: Date, defaultActiveType: QRPayloadType): QRGeneratorState => {
  const fallback = getInitialQRGeneratorState(generatedAt, defaultActiveType);
  try {
    const stored = window.sessionStorage.getItem(QR_SESSION_STORAGE_KEY);
    if (stored === null) {
      return fallback;
    }
    const parsed: unknown = JSON.parse(stored);
    if (!isPlainObject(parsed)) {
      return fallback;
    }
    if (parsed.version !== QR_SESSION_VERSION || !isPlainObject(parsed.state)) {
      return fallback;
    }
    const storedState = parsed.state;
    const restoredState = getRestoredState(fallback, storedState);

    return {
      ...restoredState,
      activeType: getRestoredActiveType(storedState.activeType, fallback.activeType),
      logo: null,
      calendarEventInput: getRestoredCalendarEventInput(fallback, restoredState),
    };
  } catch (error) {
    window.PMW.log('The QR tool could not read what was typed before the reload', error);
    return fallback;
  }
};

export const clearQRGeneratorSession = (): void => {
  try {
    window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
  } catch (error) {
    window.PMW.log('The QR tool could not clear what was typed before the reload', error);
  }
};

export const writeQRGeneratorSession = (state: QRGeneratorState): void => {
  try {
    const session: QRStoredSession = {version: QR_SESSION_VERSION, state: getStorableState(state)};
    window.sessionStorage.setItem(QR_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    window.PMW.log('The QR tool could not keep what was typed for a reload', error);
  }
};
